import { z } from "zod";
export const CUTOVER_RUNBOOK_VERSION = "2026.1";
export const CUTOVER_EVIDENCE_KINDS = [
    "discovery",
    "hardware_benchmark",
    "network_validation",
    "experience_inventory",
    "content_readiness",
    "media_preservation",
    "rollback_rehearsal",
    "cutover_window",
    "cutover_result",
    "offline_reconnect",
    "acceptance",
    "stability_exception",
    "pilot_decision",
    "enablement",
    "media_disposition",
    "screen_acceptance",
    "client_signoff",
];
export const SCREEN_CUTOVER_EVIDENCE_KINDS = [
    "discovery",
    "hardware_benchmark",
    "network_validation",
    "experience_inventory",
    "content_readiness",
    "media_preservation",
    "rollback_rehearsal",
    "cutover_window",
    "cutover_result",
    "offline_reconnect",
    "acceptance",
    "stability_exception",
    "screen_acceptance",
];
export const PROGRAM_CUTOVER_EVIDENCE_KINDS = [
    "pilot_decision",
    "enablement",
    "media_disposition",
    "client_signoff",
];
export const CUTOVER_STAGES = [
    "discovery",
    "staging",
    "ready_to_schedule",
    "scheduled",
    "observing",
    "acceptance_pending",
    "accepted",
    "rolled_back",
];
export const CUTOVER_GATE_STATES = [
    "blocked",
    "observing",
    "passed",
    "pending",
];
export const CUTOVER_GATE_KEYS = [
    "discovery",
    "hardware",
    "network",
    "inventory",
    "content",
    "media",
    "rollback",
    "window",
    "cutover",
    "offline",
    "acceptance",
    "pilot",
    "stability",
    "screen_signoff",
];
const dateTimeSchema = z.iso.datetime({ offset: true });
const uuidSchema = z.string().uuid();
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/u);
const shortTextSchema = z.string().trim().min(1).max(200);
const boundedTextSchema = z.string().trim().min(1).max(500);
function checkSet(keys) {
    const shape = Object.fromEntries(keys.map((key) => [key, z.boolean()]));
    return z.object(shape).strict();
}
export const cutoverDiscoveryEvidenceSchema = z
    .object({
    checks: checkSet(["kioskStartPointConfirmed", "touchCalibrationPassed"]),
    display: z
        .object({
        heightPx: z.number().int().min(320).max(16_384),
        orientation: z.enum(["landscape", "portrait"]),
        powerBehavior: z.enum([
            "always_on",
            "signal_loss_standby",
            "sleep_timer",
            "unknown",
        ]),
        widthPx: z.number().int().min(320).max(16_384),
    })
        .strict(),
    kind: z.literal("discovery"),
    networkType: z.enum(["ethernet", "wifi"]),
    observedAt: dateTimeSchema,
    openFollowUps: z.array(boundedTextSchema).max(20),
    physicalAccessNotes: z.string().trim().min(1).max(1_000),
    physicalLocation: shortTextSchema,
    piModel: shortTextSchema,
    schemaVersion: z.literal(1),
    storageMedium: z.enum(["non_removable", "other", "removable_sd"]),
    touchController: shortTextSchema,
    touchInterface: z.enum(["i2c", "other", "serial", "usb"]),
})
    .strict()
    .superRefine((evidence, context) => {
    const orientationMatches = (evidence.display.orientation === "landscape" &&
        evidence.display.widthPx >= evidence.display.heightPx) ||
        (evidence.display.orientation === "portrait" &&
            evidence.display.heightPx >= evidence.display.widthPx);
    if (!orientationMatches) {
        context.addIssue({
            code: "custom",
            message: "Display dimensions do not match orientation",
            path: ["display", "orientation"],
        });
    }
});
export const cutoverHardwareBenchmarkEvidenceSchema = z
    .object({
    benchmarkedAt: dateTimeSchema,
    checks: checkSet([
        "directorySearchPassed",
        "idleTransitionPassed",
        "mapInteractionPassed",
        "offlineStartupPassed",
        "routeRenderingPassed",
        "thermalStabilityPassed",
        "touchPassed",
        "videoDecodePassed",
        "watchdogRecoveryPassed",
    ]),
    classification: z.enum(["replace", "reuse", "reuse_with_media_limits"]),
    kind: z.literal("hardware_benchmark"),
    maxTemperatureCelsius: z.number().min(-20).max(125),
    mediaLimits: z.array(shortTextSchema).max(20),
    offlineStartupSeconds: z.number().int().nonnegative().max(600),
    routeCalculationMilliseconds: z.number().int().nonnegative().max(60_000),
    schemaVersion: z.literal(1),
    testedHardware: z.enum(["existing_client", "replacement_client"]),
    touchLatencyMilliseconds: z.number().int().nonnegative().max(10_000),
    upgradeQuoteStatus: z.enum([
        "approved",
        "declined",
        "not_required",
        "pending",
    ]),
})
    .strict()
    .superRefine((evidence, context) => {
    if (evidence.classification === "reuse_with_media_limits" &&
        evidence.mediaLimits.length === 0) {
        context.addIssue({
            code: "custom",
            message: "Reuse with limits requires at least one media limit",
            path: ["mediaLimits"],
        });
    }
    if (evidence.classification === "replace" &&
        evidence.upgradeQuoteStatus === "not_required") {
        context.addIssue({
            code: "custom",
            message: "Replacement hardware requires an upgrade quote decision",
            path: ["upgradeQuoteStatus"],
        });
    }
    if (evidence.classification !== "replace" &&
        evidence.upgradeQuoteStatus !== "not_required") {
        context.addIssue({
            code: "custom",
            message: "Reusable hardware cannot carry an upgrade quote",
            path: ["upgradeQuoteStatus"],
        });
    }
});
export const cutoverNetworkValidationEvidenceSchema = z
    .object({
    checks: checkSet([
        "apiReachable",
        "contentDeliveryReachable",
        "dnsResolved",
        "heartbeatReachable",
        "ntpSynchronized",
    ]),
    kind: z.literal("network_validation"),
    networkType: z.enum(["ethernet", "wifi"]),
    rssiDbm: z.number().int().min(-120).max(0).nullable(),
    schemaVersion: z.literal(1),
    sustainedMegabitsPerSecond: z.number().nonnegative().max(100_000),
    validatedAt: dateTimeSchema,
})
    .strict()
    .superRefine((evidence, context) => {
    if (evidence.networkType === "wifi" && evidence.rssiDbm === null) {
        context.addIssue({
            code: "custom",
            message: "Wi-Fi validation requires RSSI",
            path: ["rssiDbm"],
        });
    }
    if (evidence.networkType === "ethernet" && evidence.rssiDbm !== null) {
        context.addIssue({
            code: "custom",
            message: "Ethernet validation cannot include RSSI",
            path: ["rssiDbm"],
        });
    }
});
export const cutoverExperienceInventoryEvidenceSchema = z
    .object({
    attractItemCount: z.number().int().positive().max(10_000),
    buildingDetailCount: z.number().int().positive().max(10_000),
    checks: checkSet([
        "accessibilityControlsCaptured",
        "clientOwnedSourcesConfirmed",
        "emergencyBehaviorCaptured",
        "fullAttractLoopCaptured",
        "languagesCaptured",
    ]),
    directoryCategoryCount: z.number().int().positive().max(10_000),
    floorCount: z.number().int().positive().max(1_000),
    kind: z.literal("experience_inventory"),
    languageCount: z.number().int().positive().max(100),
    parityRecheckedAt: dateTimeSchema,
    profileCount: z.number().int().positive().max(1_000_000),
    representativeRouteCount: z.number().int().positive().max(100_000),
    schemaVersion: z.literal(1),
})
    .strict();
export const cutoverContentReadinessEvidenceSchema = z
    .object({
    checks: checkSet([
        "accessibleRoutePassed",
        "clientStagingApproved",
        "directoryReviewed",
        "fallbackConfigured",
        "idleAttractPassed",
        "mapRoutesValidated",
        "nonPhiConfirmed",
        "phiCapableItemsBlocked",
        "proofOfPlayObserved",
        "providerMovePassed",
        "publishedExperienceComplete",
        "targetCanvasVerified",
        "touchPreviewPassed",
    ]),
    interactivePublicationId: uuidSchema,
    kind: z.literal("content_readiness"),
    reviewedAt: dateTimeSchema,
    schedulePublicationId: uuidSchema,
    schemaVersion: z.literal(1),
})
    .strict();
export const cutoverMediaPreservationEvidenceSchema = z
    .object({
    checks: checkSet([
        "chainOfCustodyRecorded",
        "clientPropertyConfirmed",
        "originalMediaIntact",
    ]),
    kind: z.literal("media_preservation"),
    mediaLabel: z.string().trim().min(2).max(100),
    preservedAt: dateTimeSchema,
    retainedUntil: dateTimeSchema,
    schemaVersion: z.literal(1),
    storageMedium: z.enum(["encrypted_image", "original_media"]),
})
    .strict()
    .superRefine((evidence, context) => {
    if (Date.parse(evidence.retainedUntil) <= Date.parse(evidence.preservedAt)) {
        context.addIssue({
            code: "custom",
            message: "Retained media must have a future disposition date",
            path: ["retainedUntil"],
        });
    }
});
export const cutoverRollbackRehearsalEvidenceSchema = z
    .object({
    checks: checkSet([
        "backendIndependent",
        "incumbentPlaybackConfirmed",
        "rollbackTriggersAgreed",
    ]),
    completedAt: dateTimeSchema,
    durationMinutes: z.number().int().positive().max(240),
    kind: z.literal("rollback_rehearsal"),
    schemaVersion: z.literal(1),
})
    .strict();
export const cutoverWindowEvidenceSchema = z
    .object({
    checks: checkSet([
        "clientApproved",
        "escalationContactReachable",
        "offPeakConfirmed",
        "siteAccessArranged",
    ]),
    escalationContactName: shortTextSchema,
    kind: z.literal("cutover_window"),
    schemaVersion: z.literal(1),
    windowEndsAt: dateTimeSchema,
    windowStartsAt: dateTimeSchema,
})
    .strict()
    .superRefine((evidence, context) => {
    if (Date.parse(evidence.windowEndsAt) <= Date.parse(evidence.windowStartsAt)) {
        context.addIssue({
            code: "custom",
            message: "Cutover window end must follow its start",
            path: ["windowEndsAt"],
        });
    }
});
export const cutoverResultEvidenceSchema = z
    .object({
    checks: checkSet([
        "clientCompletionNotified",
        "clientStartNotified",
        "enrolledAndPlaying",
    ]),
    completedAt: dateTimeSchema,
    downtimeMinutes: z.number().int().nonnegative().max(240),
    firstDigitalFrameAt: dateTimeSchema,
    followUpPlan: z.string().trim().min(1).max(1_000).nullable(),
    kind: z.literal("cutover_result"),
    outcome: z.enum(["cutover_completed", "rolled_back"]),
    rollbackCause: z.string().trim().min(1).max(1_000).nullable(),
    schemaVersion: z.literal(1),
    startedAt: dateTimeSchema,
})
    .strict()
    .superRefine((evidence, context) => {
    const startedAt = Date.parse(evidence.startedAt);
    const firstFrameAt = Date.parse(evidence.firstDigitalFrameAt);
    const completedAt = Date.parse(evidence.completedAt);
    if (firstFrameAt < startedAt || completedAt < firstFrameAt) {
        context.addIssue({ code: "custom", message: "Cutover timestamps are out of order" });
    }
    const expectedDowntime = Math.ceil((firstFrameAt - startedAt) / 60_000);
    if (Math.abs(expectedDowntime - evidence.downtimeMinutes) > 1) {
        context.addIssue({
            code: "custom",
            message: "Downtime does not match cutover timestamps",
            path: ["downtimeMinutes"],
        });
    }
    const rolledBack = evidence.outcome === "rolled_back";
    if (rolledBack !== (evidence.rollbackCause !== null && evidence.followUpPlan !== null)) {
        context.addIssue({
            code: "custom",
            message: "Rollback cause and follow-up plan must match the outcome",
            path: ["rollbackCause"],
        });
    }
});
export const cutoverOfflineReconnectEvidenceSchema = z
    .object({
    checks: checkSet([
        "attractLoopContinued",
        "cachedDirectoryContinued",
        "cachedRoutesContinued",
        "heartbeatRecovered",
        "proofOfPlayBackfilled",
        "reconciledAfterReconnect",
    ]),
    disconnectedAt: dateTimeSchema,
    kind: z.literal("offline_reconnect"),
    reconnectedAt: dateTimeSchema,
    schemaVersion: z.literal(1),
})
    .strict()
    .superRefine((evidence, context) => {
    if (Date.parse(evidence.reconnectedAt) <= Date.parse(evidence.disconnectedAt)) {
        context.addIssue({
            code: "custom",
            message: "Reconnect must follow disconnect",
            path: ["reconnectedAt"],
        });
    }
});
export const cutoverAcceptanceEvidenceSchema = z
    .object({
    checks: checkSet([
        "accessibleRoutePassed",
        "departmentSearchPassed",
        "idleAttractPassed",
        "mapVisualPassed",
        "orientationAspectPassed",
        "playlistVisualPassed",
        "profileDestinationPassed",
        "providerSearchPassed",
        "routeOverlayPassed",
        "touchTargetsPassed",
    ]),
    kind: z.literal("acceptance"),
    schemaVersion: z.literal(1),
    verifiedAt: dateTimeSchema,
})
    .strict();
export const cutoverStabilityExceptionEvidenceSchema = z
    .object({
    clientAccepted: z.boolean(),
    documentedAt: dateTimeSchema,
    explanation: boundedTextSchema,
    kind: z.literal("stability_exception"),
    observedEventIds: z.array(uuidSchema).min(1).max(100),
    schemaVersion: z.literal(1),
})
    .strict();
export const cutoverPilotDecisionEvidenceSchema = z
    .object({
    approverName: shortTextSchema,
    decidedAt: dateTimeSchema,
    decision: z.enum(["go", "no_go"]),
    kind: z.literal("pilot_decision"),
    rationale: boundedTextSchema,
    schemaVersion: z.literal(1),
})
    .strict();
export const cutoverEnablementEvidenceSchema = z
    .object({
    attractChangeAuditEventId: uuidSchema,
    checks: checkSet([
        "approverCapacityConfirmed",
        "attractContentChangedUnassisted",
        "emergencyTestDemonstrated",
        "fleetMonitoringCovered",
        "providerMovePublishedUnassisted",
        "quickStartDelivered",
    ]),
    completedAt: dateTimeSchema,
    emergencyTestOverrideId: uuidSchema,
    kind: z.literal("enablement"),
    participantCount: z.number().int().positive().max(1_000),
    providerMoveAuditEventId: uuidSchema,
    rolesCovered: z
        .array(z.enum(["approver", "editor", "tenant_admin"]))
        .min(1)
        .max(3),
    schemaVersion: z.literal(1),
})
    .strict();
export const cutoverMediaDispositionEvidenceSchema = z
    .object({
    action: z.enum(["disposed", "returned_to_client"]),
    checks: checkSet(["incumbentImagesDeleted", "writtenInstructionConfirmed"]),
    completedAt: dateTimeSchema,
    kind: z.literal("media_disposition"),
    mediaLabels: z.array(z.string().trim().min(2).max(100)).min(1).max(100),
    schemaVersion: z.literal(1),
})
    .strict();
export const cutoverScreenAcceptanceEvidenceSchema = z
    .object({
    acceptedAt: dateTimeSchema,
    approverName: shortTextSchema,
    confirmation: z.literal("ACCEPT SCREEN"),
    kind: z.literal("screen_acceptance"),
    schemaVersion: z.literal(1),
})
    .strict();
export const cutoverClientSignoffEvidenceSchema = z
    .object({
    approverName: shortTextSchema,
    approverTitle: shortTextSchema,
    confirmation: z.literal("ACCEPT MIGRATION"),
    evidencePackSha256: sha256Schema,
    kind: z.literal("client_signoff"),
    schemaVersion: z.literal(1),
    signedAt: dateTimeSchema,
})
    .strict();
export const cutoverEvidenceSchema = z.discriminatedUnion("kind", [
    cutoverDiscoveryEvidenceSchema,
    cutoverHardwareBenchmarkEvidenceSchema,
    cutoverNetworkValidationEvidenceSchema,
    cutoverExperienceInventoryEvidenceSchema,
    cutoverContentReadinessEvidenceSchema,
    cutoverMediaPreservationEvidenceSchema,
    cutoverRollbackRehearsalEvidenceSchema,
    cutoverWindowEvidenceSchema,
    cutoverResultEvidenceSchema,
    cutoverOfflineReconnectEvidenceSchema,
    cutoverAcceptanceEvidenceSchema,
    cutoverStabilityExceptionEvidenceSchema,
    cutoverPilotDecisionEvidenceSchema,
    cutoverEnablementEvidenceSchema,
    cutoverMediaDispositionEvidenceSchema,
    cutoverScreenAcceptanceEvidenceSchema,
    cutoverClientSignoffEvidenceSchema,
]);
function allChecksPass(checks) {
    return Object.values(checks).every(Boolean);
}
export function cutoverEvidencePassed(evidence) {
    switch (evidence.kind) {
        case "discovery":
            return (allChecksPass(evidence.checks) &&
                evidence.display.powerBehavior !== "unknown" &&
                evidence.openFollowUps.length === 0);
        case "hardware_benchmark": {
            const commonChecks = Object.entries(evidence.checks)
                .filter(([key]) => key !== "videoDecodePassed")
                .every(([, value]) => value);
            if (!commonChecks ||
                evidence.maxTemperatureCelsius > 80 ||
                evidence.offlineStartupSeconds > 90 ||
                evidence.routeCalculationMilliseconds > 1_000 ||
                evidence.touchLatencyMilliseconds > 100) {
                return false;
            }
            if (evidence.classification === "replace") {
                return (evidence.testedHardware === "replacement_client" &&
                    evidence.upgradeQuoteStatus === "approved" &&
                    evidence.checks.videoDecodePassed);
            }
            if (evidence.classification === "reuse_with_media_limits") {
                return evidence.mediaLimits.length > 0;
            }
            return evidence.checks.videoDecodePassed;
        }
        case "network_validation":
            return (allChecksPass(evidence.checks) &&
                evidence.sustainedMegabitsPerSecond >= 10 &&
                (evidence.networkType === "ethernet" ||
                    (evidence.rssiDbm !== null && evidence.rssiDbm >= -67)));
        case "experience_inventory":
        case "content_readiness":
        case "media_preservation":
        case "offline_reconnect":
        case "acceptance":
        case "media_disposition":
            return allChecksPass(evidence.checks);
        case "stability_exception":
            return evidence.clientAccepted;
        case "rollback_rehearsal":
            return allChecksPass(evidence.checks) && evidence.durationMinutes <= 30;
        case "cutover_window":
            return allChecksPass(evidence.checks);
        case "cutover_result":
            return (evidence.outcome === "cutover_completed" &&
                evidence.downtimeMinutes <= 60 &&
                allChecksPass(evidence.checks));
        case "pilot_decision":
            return evidence.decision === "go";
        case "enablement":
            return allChecksPass(evidence.checks) && new Set(evidence.rolesCovered).size === 3;
        case "screen_acceptance":
        case "client_signoff":
            return true;
    }
}
export function isScreenCutoverEvidence(evidence) {
    return SCREEN_CUTOVER_EVIDENCE_KINDS.includes(evidence.kind);
}
export const createCutoverProgramCommandSchema = z
    .object({
    name: z.string().trim().min(3).max(200),
    pilotScreenId: uuidSchema,
    programId: uuidSchema,
    screenIds: z.array(uuidSchema).min(1).max(100),
})
    .strict()
    .superRefine((command, context) => {
    if (new Set(command.screenIds).size !== command.screenIds.length) {
        context.addIssue({
            code: "custom",
            message: "Screen IDs must be unique",
            path: ["screenIds"],
        });
    }
    if (!command.screenIds.includes(command.pilotScreenId)) {
        context.addIssue({
            code: "custom",
            message: "Pilot screen must be part of the cutover program",
            path: ["pilotScreenId"],
        });
    }
});
export const recordCutoverEvidenceCommandSchema = z
    .object({
    evidence: cutoverEvidenceSchema,
    evidenceId: uuidSchema,
    expectedProgramVersion: z.number().int().positive(),
    screenCutoverId: uuidSchema.nullable(),
})
    .strict()
    .superRefine((command, context) => {
    if (isScreenCutoverEvidence(command.evidence) !== (command.screenCutoverId !== null)) {
        context.addIssue({
            code: "custom",
            message: "Evidence scope does not match screenCutoverId",
            path: ["screenCutoverId"],
        });
    }
});
export const signOffCutoverProgramCommandSchema = z
    .object({
    approverName: shortTextSchema,
    approverTitle: shortTextSchema,
    confirmation: z.literal("ACCEPT MIGRATION"),
    evidenceId: uuidSchema,
    expectedProgramVersion: z.number().int().positive(),
    packId: uuidSchema,
})
    .strict();
export const cutoverEvidenceRecordSchema = z
    .object({
    createdAt: dateTimeSchema,
    evidence: cutoverEvidenceSchema,
    id: uuidSchema,
    payloadSha256: sha256Schema,
    programId: uuidSchema,
    recordedByMembershipId: uuidSchema,
    screenCutoverId: uuidSchema.nullable(),
    sequence: z.number().int().positive(),
})
    .strict();
export const cutoverGateSchema = z
    .object({
    detail: z.string().trim().min(1).max(500),
    key: z.enum(CUTOVER_GATE_KEYS),
    source: z.enum(["platform", "recorded"]),
    state: z.enum(CUTOVER_GATE_STATES),
})
    .strict();
export const cutoverAutomatedEvidenceSchema = z
    .object({
    content: z
        .object({
        fallbackConfigured: z.boolean(),
        interactivePublished: z.boolean(),
        schedulePublished: z.boolean(),
    })
        .strict(),
    device: z
        .object({
        activeAlertCount: z.number().int().nonnegative(),
        health: z.enum(["degraded", "healthy", "offline", "unpaired"]),
        id: uuidSchema.nullable(),
        lastSeenAt: dateTimeSchema.nullable(),
    })
        .strict(),
    proofOfPlay: z
        .object({
        deviationCount: z.number().int().nonnegative(),
        eventCount: z.number().int().nonnegative(),
    })
        .strict(),
    stability: z
        .object({
        blockingEventCount: z.number().int().nonnegative(),
        elapsedHours: z.number().nonnegative(),
        endsAt: dateTimeSchema.nullable(),
        ready: z.boolean(),
        startedAt: dateTimeSchema.nullable(),
    })
        .strict(),
})
    .strict();
export const cutoverScreenReferenceSchema = z
    .object({
    id: uuidSchema,
    name: z.string().trim().min(1).max(200),
    siteId: uuidSchema.nullable(),
    siteName: z.string().trim().min(1).max(200).nullable(),
    target: z
        .object({
        heightPx: z.number().int().positive(),
        orientation: z.enum(["landscape", "portrait"]),
        widthPx: z.number().int().positive(),
    })
        .strict(),
})
    .strict();
export const screenCutoverSchema = z
    .object({
    automatedEvidence: cutoverAutomatedEvidenceSchema,
    blockers: z.array(z.string().trim().min(1).max(500)).readonly(),
    createdAt: dateTimeSchema,
    evidence: z.array(cutoverEvidenceRecordSchema).readonly(),
    gates: z.array(cutoverGateSchema).readonly(),
    id: uuidSchema,
    isPilot: z.boolean(),
    screen: cutoverScreenReferenceSchema,
    stage: z.enum(CUTOVER_STAGES),
})
    .strict();
export const cutoverProgramSchema = z
    .object({
    createdAt: dateTimeSchema,
    evidencePackSha256: sha256Schema.nullable(),
    id: uuidSchema,
    name: z.string().trim().min(3).max(200),
    runbookVersion: z.literal(CUTOVER_RUNBOOK_VERSION),
    signedOffAt: dateTimeSchema.nullable(),
    status: z.enum(["active", "signed_off"]),
    updatedAt: dateTimeSchema,
    version: z.number().int().positive(),
})
    .strict();
export const cutoverPilotGateSchema = z
    .object({
    blockers: z.array(z.string().trim().min(1).max(500)).readonly(),
    observedHours: z.number().nonnegative(),
    requiredHours: z.number().int().positive(),
    state: z.enum([
        "go",
        "no_go",
        "not_started",
        "observing",
        "prerequisites",
        "ready_for_decision",
    ]),
})
    .strict();
export const cutoverWorkspaceSchema = z
    .object({
    availableScreens: z.array(cutoverScreenReferenceSchema).readonly(),
    generatedAt: dateTimeSchema,
    pilotGate: cutoverPilotGateSchema.nullable(),
    program: cutoverProgramSchema.nullable(),
    programEvidence: z.array(cutoverEvidenceRecordSchema).readonly(),
    screens: z.array(screenCutoverSchema).readonly(),
    signoffBlockers: z.array(z.string().trim().min(1).max(500)).readonly(),
    signoffReady: z.boolean(),
})
    .strict();
export const cutoverEvidencePackContentSchema = z
    .object({
    generatedAt: dateTimeSchema,
    program: cutoverProgramSchema,
    programEvidence: z.array(cutoverEvidenceRecordSchema).readonly(),
    schemaVersion: z.literal(1),
    screens: z.array(screenCutoverSchema).readonly(),
})
    .strict();
export const cutoverEvidencePackSchema = z
    .object({
    content: cutoverEvidencePackContentSchema,
    fileName: z.string().regex(/^[A-Za-z0-9._-]+\.json$/u),
    sha256: sha256Schema,
})
    .strict();
function canonicalValue(value) {
    if (Array.isArray(value))
        return value.map(canonicalValue);
    if (value !== null && typeof value === "object") {
        return Object.fromEntries(Object.entries(value)
            .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
            .map(([key, entry]) => [key, canonicalValue(entry)]));
    }
    return value;
}
export function canonicalCutoverValue(value) {
    return JSON.stringify(canonicalValue(value));
}
//# sourceMappingURL=cutover.js.map